import Button from "@Components/Button";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
import FallCalendar from "@Icons/FallCalendar";
import useRoomTypeContext from "@Pages/PropertyPage/components/RoomTypeSection/context/useRoomTypeContext";
import {
  FUTURE_INTAKE_COPY_KEYS,
  getIntakeCopyParams,
  getIntakeName,
} from "@Pages/PropertyPage/utils/futureIntake";
import { AppState } from "@Store/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useTranslationHook from "hooks/useTranslationHook";
import { useSelector } from "react-redux";
import useApplyForFallExperiment from "../../useApplyForFallExperiment";
import useBookClick from "../useBookClick";
import styles from "./FutureIntakeStrip.module.scss";

type FutureIntakeStripProps = {
  roomTypeData: any;
  featuredImage: any;
  isDesktop?: boolean;
  /** Where the strip lives, for tracking: room_card | room_details_side_panel | room_details_modal. */
  sectionName: string;
  /**
   * "card" = the strip under the room card's amenities; "details" = the bordered card above the
   * tenancies in the room details side panel / modal (Figma 5220-61717).
   */
  layout?: "card" | "details";
  /**
   * Wraps the strip in a div with this class: a row of the parent list that takes the list's row
   * spacing, so the strip itself keeps its own padding and border. Not rendered without an intake.
   */
  slotClassName?: string;
};

/**
 * The "Apply for Fall / Spring" strip. Renders only when `useApplyForFallExperiment` returns an
 * intake for the room, so the card, the side panel and the modal show it under the same rules.
 */
const FutureIntakeStrip = ({
  roomTypeData,
  featuredImage,
  isDesktop = false,
  sectionName,
  layout = "card",
  slotClassName,
}: FutureIntakeStripProps) => {
  const { t } = useTranslationHook();
  const customWindowOpen = useCustomWindowOpen();
  const { propertyData } = useRoomTypeContext();
  const { onBookClick } = useBookClick(roomTypeData, customWindowOpen);
  const futureIntake = useApplyForFallExperiment(roomTypeData, propertyData);
  const isApplied = useSelector(({ userHistory }: AppState) =>
    ["Waitlisted", "Contacted"].includes(
      userHistory.inventoriesData?.children?.[String(roomTypeData?.id)]?.label || "",
    ),
  );

  if (!futureIntake) return null;

  const isDetails = layout === "details";
  const copyKeys = FUTURE_INTAKE_COPY_KEYS[futureIntake.key];
  const copyParams = getIntakeCopyParams(futureIntake);

  const getSubtitle = () => {
    if (!isDesktop) return t("propertypage.roomtypesectiondynamic.roomtypecard.saveyourspot");
    if (isDetails) return t("propertypage.futureintake.submitearly");
    return t("propertypage.components.tenancycard.saveYourSpotEarly");
  };

  const getCTA = () => {
    if (isApplied) return t("propertypage.components.tenancycard.continueApply1");
    if (isDesktop) return t(copyKeys.cta, copyParams);
    return t("propertypage.roomtypesectiondynamic.roomtypecard.continueapply2");
  };

  const handleClick = () => {
    const intakeName = getIntakeName(futureIntake);
    gtagEventEmitter({
      action: "clicked_apply_for_fall",
      category: PAGE.INVENTORY,
      room_id: roomTypeData.id,
      cta_text: `Apply for ${intakeName}`,
      intake_name: intakeName,
      section_name: sectionName,
    });
    try {
      const tenancyBookObj = JSON.parse(JSON.stringify(roomTypeData));
      tenancyBookObj.config = JSON.parse(JSON.stringify(roomTypeData));
      tenancyBookObj.images = [featuredImage];
      // future_intake_name rides on the lead form's inventory to the lead meta.
      onBookClick(
        {
          ...tenancyBookObj,
          pricing: {},
          config: { ...tenancyBookObj.config, pricing: {} },
          future_intake_name: intakeName,
        },
        "Apply For Fall",
        false,
        roomTypeData.id,
        sectionName,
      );
    } catch (err) {
      onBookClick(
        {
          ...roomTypeData,
          pricing: {},
          config: { ...roomTypeData, pricing: {}, images: [featuredImage] },
          future_intake_name: intakeName,
        },
        "Apply For Fall",
        false,
        roomTypeData.id,
        sectionName,
      );
    }
  };

  const strip = (
    <div
      className={formatClassNames(
        styles.applyForFallContainer,
        !isDesktop && styles.mobile,
        isDetails && styles.details,
      )}
      data-testid={`future-intake-strip-${sectionName}`}
    >
      <div className={styles.textContainer}>
        <FallCalendar height={isDesktop ? 32 : 24} width={isDesktop ? 32 : 24} />
        <div>
          <div className={styles.titleText}>
            {t(isDesktop ? copyKeys.titleDesktop : copyKeys.titleMobile, copyParams)}
          </div>
          <div className={styles.subTitleText}>{getSubtitle()}</div>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        className={styles.fallBtn}
        disable={isApplied}
        onClick={handleClick}
      >
        {getCTA()}
      </Button>
    </div>
  );

  return slotClassName ? <div className={slotClassName}>{strip}</div> : strip;
};

// Same variant reach as the room card it came out of (RoomTypeCardV2): China is in scope (the hook
// shows it there without GrowthBook), and the whitelabel default is kept so the card is unchanged.
export default wrapperHOC(FutureIntakeStrip, {
  componentName: "FutureIntakeStrip-RoomTypeCard-RoomTypeSection-PropertyPage",
  showForChina: true,
  showForWhiteLable: true,
});
